import AccordionFilter from '@allesauto/core/accordionFilter';
import ColorList from '@allesauto/core/colorList';
import { DropdownItemType } from '@allesauto/core/dropdown';
import { type Option } from '@allesauto/core/searchDropdown';
import { useWhiteLabel } from '@allesauto/core/whiteLabel';
import { SchemaTypes } from '@allesauto/graphql';
import translate from '@allesauto/i18n';
import { consts, GTMFilterCategory } from '@allesauto/utils/consts';
import isArray from 'lodash/isArray';
import isNil from 'lodash/isNil';
import { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import DriveFilter from '@/components/PCP/Filters/DriveFilter/DriveFilter';
import FuelFilter from '@/components/PCP/Filters/FuelFilter/FuelFilter';
import InteriorFilter from '@/components/PCP/Filters/InteriorFilter/InteriorFilter';
import LocationFilter from '@/components/PCP/Filters/LocationFilter/LocationFilter';
import MakeModelFilter from '@/components/PCP/Filters/MakeModelFilter/MakeModelFilter';
import VehicleTypeFilter from '@/components/PCP/Filters/VehicleTypeFilter/VehicleTypeFilter';

import { useGTM } from '@/global/hooks/useGTM';
import { RootState } from '@/store/redux';
import {
  EquipmentFilterStateProps,
  MerchantLocationFilterStateProps,
  resetFilterLocation,
  setEquipmentFilter,
  setFilterBodyColors,
  setFilterEmission,
  setFilterFuel,
  setFilterInterior,
  setFilterLocation,
  setFilterVehicleCondition,
  setFilterVehicleType,
  setMerchantLocationFilter,
  setPageNumber,
} from '@/store/redux/actions/pages/pcp';
import { setDriveFilters } from '@/store/redux/actions/pages/pcp/filters/drive';
import { setPriceFilterRange } from '@/store/redux/actions/pages/pcp/filters/price';
import { PCPCombinedState } from '@/store/redux/reducers/pages/pcp';
import { VehicleConditionStateProps } from '@/store/redux/reducers/pages/pcp/filters/vehicleCondition';

import EmissionFilter from '../PCP/Filters/EmissionFilter/EmissionFilter';
import EquipmentFilter from '../PCP/Filters/EquipmentFilter/EquipmentFilter';
import MerchantLocationFilter from '../PCP/Filters/MerchantLocationFilter/MerchantLocationFilter';
import PriceFilter from '../PCP/Filters/PriceFilter/PriceFilter';
import VehicleConditionFilter from '../PCP/Filters/VehicleConditionFilter/VehicleConditionFilter';

export interface ISidebar {
  locationFilterProps: {
    location?: Option;
    radius: number;
  };
  makeModelFilterProps: {
    makeItems: DropdownItemType[];
  };
  colorListFilterProps: {
    colors: SchemaTypes.InteriorColor[];
  };
  interiorFilterProps: {
    types: SchemaTypes.InteriorType[];
    colors: SchemaTypes.InteriorColor[];
  };
  priceFilterProps: {
    priceRanges: SchemaTypes.PriceRange[];
  };
  equipmentFilterProps: {
    equipments: SchemaTypes.Equipment;
  };
  merchantLocationFilterProps: {
    merchantLocations: {
      merchantLocation: SchemaTypes.MerchantFacet[];
    };
  };
  fuelFilterProps: SchemaTypes.FuelTypeFacet;
  driveFilterProps: SchemaTypes.DriveFacet;
  emissionFilterProps: {
    emissionClass: SchemaTypes.EmissionClass[];
    emissionSticker: SchemaTypes.EmissionSticker[];
    co2Efficiency: SchemaTypes.Co2Efficiency[];
    minCo2Emission: SchemaTypes.EmissionFacet['minCo2Emission'];
    maxCo2Emission: SchemaTypes.EmissionFacet['maxCo2Emission'];
    particleFilter: SchemaTypes.EmissionClass;
  };
  vehicleConditionFilterProps: {
    conditions: SchemaTypes.VehicleConditionFacet;
  };
  vehicleTypeFilterProps: {
    vehicleType: SchemaTypes.VehicleType[];
    doors: SchemaTypes.Door[];
    minSeats: number;
    maxSeats: number;
  };
  merchantUuids: SchemaTypes.MerchantFacet[];
}

const Sidebar = ({
  makeModelFilterProps,
  colorListFilterProps,
  locationFilterProps,
  priceFilterProps,
  interiorFilterProps,
  equipmentFilterProps,
  merchantLocationFilterProps,
  vehicleConditionFilterProps,
  driveFilterProps,
  fuelFilterProps,
  emissionFilterProps,
  vehicleTypeFilterProps,
  merchantUuids,
}: ISidebar): JSX.Element => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const GTM = useGTM();
  const whiteLabel = useWhiteLabel();

  const reduxInteriorFilterState = useSelector(
    (state: any) => state.pages.pcp.filters.interior
  );

  const reduxInteriorFilterCount = useSelector(
    (state: any) => state.pages.pcp.filters.interior.filterCount || 0
  );

  const reduxLocationFilterLocationCount = useSelector(
    (state: any) => state.pages.pcp.filters.location.filterCount || 0
  );

  const reduxMakeModelFilterCount = useSelector(
    (state: any) => state.pages.pcp.filters.makeModel.filterCount || 0
  );

  const reduxBodyColorsState = useSelector(
    (state: any) => state.pages.pcp.filters.bodyColors.colors
  );

  const reduxPriceFilterRange = useSelector(
    (state: any) => state.pages.pcp.filters.priceRange
  );

  const reduxPriceFilterCount = useMemo(() => {
    if (reduxPriceFilterRange) {
      const defaultPriceRange = priceFilterProps.priceRanges.find(
        (pr) => pr.key === reduxPriceFilterRange.key
      );

      if (defaultPriceRange) {
        return reduxPriceFilterRange.min !== defaultPriceRange.min ||
          reduxPriceFilterRange.max !== defaultPriceRange.max
          ? 1
          : 0;
      }
    }

    return 0;
  }, [reduxPriceFilterRange, priceFilterProps]);

  const reduxVehicleConditionFilterState = useSelector(
    (state: any) => state.pages.pcp.filters.vehicleCondition
  );

  const reduxVehicleConditionFilterCount = useSelector(
    (state: any) => state.pages.pcp.filters.vehicleCondition.filterCount || 0
  );

  const reduxBodyColorsFilterCount = useSelector(
    (state: any) => state.pages.pcp.filters.bodyColors.filterCount || 0
  );

  const reduxDriveFilterState = useSelector(
    (state: any) => state.pages.pcp.filters.drive
  );

  const reduxDriveFilterCount = useMemo(() => {
    if (reduxDriveFilterState) {
      const { power: defaultPowers, maxCubicCapacity: defaultCapacity } =
        driveFilterProps ?? {};

      const {
        gearBox = [],
        power = {},
        maxCubicCapacity = 0,
      } = reduxDriveFilterState;

      const defaultPower = (defaultPowers ?? []).find(
        (pr) => pr?.key === power.key
      );
      const defaultPowerMin = !isNil(defaultPower?.min)
        ? Math.ceil(defaultPower!.min / 10) * 10
        : null;
      const defaultPowerMax = !isNil(defaultPower?.max)
        ? Math.floor(defaultPower!.max / 10) * 10
        : null;
      const defaultMaxCapacity = Math.floor(defaultCapacity / 500) * 500;

      return (
        gearBox.length +
        (power.min !== null &&
        power.max !== null &&
        (power.min !== defaultPowerMin || power.max !== defaultPowerMax)
          ? 1
          : 0) +
        (maxCubicCapacity !== null && maxCubicCapacity !== defaultMaxCapacity
          ? 1
          : 0)
      );
    }

    return 0;
  }, [reduxDriveFilterState, driveFilterProps]);

  const reduxFuelFilterState = useSelector(
    (state: RootState) => (state.pages.pcp as PCPCombinedState).filters.fuelType
  );

  const reduxEmissionFilterState = useSelector(
    (state: RootState) => (state.pages.pcp as PCPCombinedState).filters.emission
  );

  const reduxFuelFilterCount = useMemo(() => {
    if (reduxFuelFilterState) {
      const { maxFuelConsumption, maxPowerConsumption } = fuelFilterProps ?? {};

      return (
        (isArray(reduxFuelFilterState.fuelType)
          ? reduxFuelFilterState.fuelType.length
          : 0) +
        (!isNil(reduxFuelFilterState.maxFuelConsumption) &&
        reduxFuelFilterState.maxFuelConsumption !== maxFuelConsumption
          ? 1
          : 0) +
        (!isNil(reduxFuelFilterState.maxPowerConsumption) &&
        reduxFuelFilterState.maxPowerConsumption !== maxPowerConsumption
          ? 1
          : 0)
      );
    }

    return 0;
  }, [reduxFuelFilterState, fuelFilterProps]);

  const reduxEmissionFilterCount = useMemo(() => {
    if (reduxEmissionFilterState) {
      const { minCo2Emission, maxCo2Emission } = emissionFilterProps || {};

      return (
        (!isNil(reduxEmissionFilterState.emissionClass) ? 1 : 0) +
        (isArray(reduxEmissionFilterState.emissionSticker)
          ? reduxEmissionFilterState.emissionSticker.length
          : 0) +
        (!isNil(reduxEmissionFilterState.co2Efficiency) ? 1 : 0) +
        (!isNil(reduxEmissionFilterState.minCo2Emission) &&
        reduxEmissionFilterState.minCo2Emission !== minCo2Emission
          ? 1
          : 0) +
        (!isNil(reduxEmissionFilterState.maxCo2Emission) &&
        reduxEmissionFilterState.maxCo2Emission !== maxCo2Emission
          ? 1
          : 0) +
        (reduxEmissionFilterState.particleFilter ? 1 : 0)
      );
    }
    return 0;
  }, [reduxEmissionFilterState, emissionFilterProps]);

  const reduxVehicleTypeFilter = useSelector(
    (state: any) => state.pages.pcp.filters.vehicleType
  );

  const reduxVehicleTypeFilterCount = useSelector(
    (state: any) => state.pages.pcp.filters.vehicleType.filterCount || 0
  );

  const getDropdownEquipmentOptions = (
    arrayOfBackendValues:
      | SchemaTypes.Equipment['comfort']
      | SchemaTypes.Equipment['entertainment']
      | SchemaTypes.Equipment['security'],
    names: string[]
  ) => {
    return (arrayOfBackendValues || [])
      .map((item: any) => {
        return item && { value: item.name, label: item.displayName };
      })
      .filter((item: any) => item && names.indexOf(item.value) !== -1);
  };

  const getDropdownMerchantLocationOptions = (
    arrayOfBackendValues: SchemaTypes.MerchantFacet[],
    names: string[]
  ) => {
    return (arrayOfBackendValues || [])
      .map((item: any) => {
        return item && { value: item.name, label: item.displayName };
      })
      .filter((item: any) => item && names.indexOf(item.value) !== -1);
  };

  const reduxEquipmentFilterState = useSelector((state: any) => {
    const comfort = getDropdownEquipmentOptions(
      equipmentFilterProps.equipments.comfort,
      state.pages.pcp.filters.equipment.comfort
    );
    const entertainment = getDropdownEquipmentOptions(
      equipmentFilterProps.equipments.entertainment,
      state.pages.pcp.filters.equipment.entertainment
    );
    const security = getDropdownEquipmentOptions(
      equipmentFilterProps.equipments.security,
      state.pages.pcp.filters.equipment.security
    );

    return (
      {
        aircon: state.pages.pcp.filters.equipment.aircon,
        comfort,
        entertainment,
        security,
      } || {}
    );
  });

  const reduxMerchantLocationFilterState = useSelector((state: any) => {
    const merchantLocation = getDropdownMerchantLocationOptions(
      merchantLocationFilterProps.merchantLocations.merchantLocation,
      state.pages.pcp.filters.merchantLocation.merchantLocation
    );

    return (
      {
        merchantLocation,
      } || {}
    );
  });

  const reduxEquipmentFilterCount = useMemo(() => {
    if (reduxEquipmentFilterState) {
      return (
        (isArray(reduxEquipmentFilterState.aircon)
          ? reduxEquipmentFilterState.aircon.length
          : 0) +
        (isArray(reduxEquipmentFilterState.comfort)
          ? reduxEquipmentFilterState.comfort.length
          : 0) +
        (isArray(reduxEquipmentFilterState.security)
          ? reduxEquipmentFilterState.security.length
          : 0) +
        (isArray(reduxEquipmentFilterState.entertainment)
          ? reduxEquipmentFilterState.entertainment.length
          : 0)
      );
    }
    return 0;
  }, [reduxEquipmentFilterState]);

  const reduxMerchantLocationFilterCount = useMemo(() => {
    if (reduxMerchantLocationFilterState) {
      return isArray(reduxMerchantLocationFilterState.merchantLocation)
        ? reduxMerchantLocationFilterState.merchantLocation.length
        : 0;
    }
    return 0;
  }, [reduxMerchantLocationFilterState]);

  const onLocationFilterChange = useCallback(
    (value: any) => {
      if (value && (value.location || value.radius)) {
        dispatch(setFilterLocation(value));
        GTM.setFilterInformation(GTMFilterCategory.LOCATION, value);
      } else {
        dispatch(resetFilterLocation());
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const onInteriorFilterChange = useCallback(
    ({ types, colors }: { types: any; colors: any }) => {
      dispatch(
        setFilterInterior({
          interiorType: types,
          interiorColor: colors,
        })
      );
      GTM.setFilterInformation(GTMFilterCategory.INTERIOR, { types, colors });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dispatch]
  );

  const onVehicleTypeFilterChange = useCallback(
    (selectedType: any) => {
      dispatch(setFilterVehicleType(selectedType));
      GTM.setFilterInformation(GTMFilterCategory.VEHICLE_TYPE, selectedType);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dispatch]
  );

  const onPriceFilterChange = useCallback(
    (range: SchemaTypes.PriceRange) => {
      // When populating the filter on page load, don't push to the data layer
      // Same applies to pushing only one event if the price max or min values change to avoid event
      // duplication
      const isPriceChange =
        reduxPriceFilterRange.max !== 0 &&
        reduxPriceFilterRange.min !== 0 &&
        (reduxPriceFilterRange.max !== range.max ||
          reduxPriceFilterRange.min !== range.min);

      if (isPriceChange) {
        GTM.setFilterInformation(GTMFilterCategory.PRICE, range);
      }

      if (isPriceChange) {
        dispatch(setPageNumber(1));
      }

      dispatch(
        setPriceFilterRange({
          key: range.key,
          max: range.max ?? 0,
          min: range.min ?? 0,
        })
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dispatch, reduxPriceFilterRange]
  );

  const maxOwnersDefault = useSelector(
    (state: any) => state.pages.pcp.facets.vehicleCondition.maxOwners
  );

  const onVehicleConditionFilterChange = useCallback(
    ({
      condition,
      mileage,
      minYear,
      maxYear,
      maxOwners,
    }: VehicleConditionStateProps) => {
      dispatch(
        setFilterVehicleCondition({
          condition,
          mileage,
          minYear,
          maxYear,
          // only set maxOwners filter if it differs from the default
          ...(maxOwners !== maxOwnersDefault && { maxOwners }),
        })
      );

      GTM.setFilterInformation(GTMFilterCategory.VEHICLE_CONDITION, {
        condition,
        mileage,
        minYear,
        maxYear,
        maxOwners,
      });
    },

    // eslint-disable-next-line react-hooks/exhaustive-deps
    [maxOwnersDefault, dispatch]
  );

  const onFuelFilterChange = useCallback(
    (value: any) => {
      dispatch(setFilterFuel(value));
      GTM.setFilterInformation(GTMFilterCategory.FUEL, value);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dispatch]
  );

  const onEmissionFilterChange = useCallback(
    (value: any) => {
      dispatch(setFilterEmission(value));
      GTM.setFilterInformation(GTMFilterCategory.EMISSION, value);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dispatch]
  );

  const onEquipmentFilterChange = useCallback(
    (v: any) => {
      dispatch(
        setEquipmentFilter({
          aircon: v.aircon ?? [],
          comfort:
            v.comfort?.map((comfortVal: Option) => comfortVal.value) ?? [],
          security:
            v.security?.map((securityVal: Option) => securityVal.value) ?? [],
          entertainment:
            v.entertainment?.map(
              (entertainmentVal: Option) => entertainmentVal.value
            ) ?? [],
        })
      );
      GTM.setFilterInformation(GTMFilterCategory.EQUIPMENT, v);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dispatch]
  );

  const onMerchantLocationFilterChange = useCallback(
    (v: any) => {
      dispatch(
        setMerchantLocationFilter({
          merchantLocation:
            v.merchantLocation?.map(
              (merchantLocationVal: Option) => merchantLocationVal.value
            ) ?? [],
        })
      );
      GTM.setFilterInformation(GTMFilterCategory.MERCHANT_LOCATION, v);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dispatch]
  );

  const onDriveFilterChange = useCallback(
    (drive: any) => {
      dispatch(setDriveFilters(drive));
      GTM.setFilterInformation(GTMFilterCategory.DRIVE, drive);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dispatch]
  );

  const onBodyColorsFilterChange = useCallback(
    (colors: string[]) => {
      dispatch(setFilterBodyColors({ colors }));
      GTM.setFilterInformation(GTMFilterCategory.BODY_COLORS, colors);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dispatch]
  );

  return (
    <>
      {whiteLabel && merchantUuids && merchantUuids.length > 1 ? (
        <AccordionFilter
          title={translate(intl, 'ui.PCP.FILTERS.LOCATION.ACCORDION_TITLE')}
          id="FacetMerchantLocation"
          trackingId="merchantLocation"
          counter={reduxMerchantLocationFilterCount}
        >
          <MerchantLocationFilter
            onMerchantLocationFilterChange={onMerchantLocationFilterChange}
            merchantLocations={merchantLocationFilterProps.merchantLocations}
            selected={
              reduxMerchantLocationFilterState as MerchantLocationFilterStateProps
            }
          />
        </AccordionFilter>
      ) : (
        !whiteLabel && (
          <AccordionFilter
            title={translate(intl, 'ui.PCP.FILTERS.LOCATION.ACCORDION_TITLE')}
            id="FacetLocation"
            trackingId="location"
            counter={reduxLocationFilterLocationCount}
          >
            <LocationFilter
              location={locationFilterProps.location as Option}
              radius={locationFilterProps.radius}
              onLocationFilterChange={onLocationFilterChange}
            />
          </AccordionFilter>
        )
      )}
      {vehicleTypeFilterProps && (
        <AccordionFilter
          title={translate(intl, 'ui.PCP.FILTERS.VEHICLE_TYPE.FILTER_LABEL')}
          id="FacetVehicleType"
          trackingId="vehicle-type"
          counter={reduxVehicleTypeFilterCount}
        >
          <VehicleTypeFilter
            vehicleTypes={vehicleTypeFilterProps.vehicleType}
            doors={vehicleTypeFilterProps.doors as SchemaTypes.Door[]}
            maxSeats={vehicleTypeFilterProps.maxSeats}
            selected={reduxVehicleTypeFilter}
            onChange={onVehicleTypeFilterChange}
          />
        </AccordionFilter>
      )}
      {makeModelFilterProps && makeModelFilterProps.makeItems && (
        <AccordionFilter
          title={translate(intl, 'ui.PCP.FILTERS.MAKE_MODEL.FILTER_LABEL')}
          id="FacetMakeModel"
          trackingId="make-model-trim"
          counter={reduxMakeModelFilterCount}
        >
          <MakeModelFilter
            makeItems={makeModelFilterProps?.makeItems}
            makeLabel={translate(intl, 'ui.PCP.FILTERS.MAKE_MODEL.MAKE_LABEL')}
            modelLabel={translate(
              intl,
              'ui.PCP.FILTERS.MAKE_MODEL.MODEL_LABEL'
            )}
            addBtnText={translate(
              intl,
              'ui.PCP.FILTERS.MAKE_MODEL.ADD_BTN_LABEL'
            )}
            removeBtnText={translate(
              intl,
              'ui.PCP.FILTERS.MAKE_MODEL.REMOVE_BTN_LABEL'
            )}
            maxFilterLength={consts.PCP.FILTERS.MAKE_MODEL_LEVEL_MAX_ITEMS}
          />
        </AccordionFilter>
      )}
      {priceFilterProps && priceFilterProps.priceRanges && (
        <AccordionFilter
          title={translate(intl, 'ui.PCP.FILTERS.PRICE.ACCORDION_TITLE')}
          id="FacetPrice"
          trackingId="price"
          counter={reduxPriceFilterCount}
          defaultOpen
        >
          <PriceFilter
            priceRanges={priceFilterProps.priceRanges}
            range={reduxPriceFilterRange}
            onChange={onPriceFilterChange}
            extraClass="font-md"
            darkBg
          />
        </AccordionFilter>
      )}
      {vehicleConditionFilterProps?.conditions && (
        <AccordionFilter
          title={translate(
            intl,
            'ui.PCP.FILTERS.VEHICLE_CONDITION.ACCORDION_TITLE'
          )}
          id="FacetVehicleCondition"
          trackingId="condition"
          counter={reduxVehicleConditionFilterCount}
        >
          <VehicleConditionFilter
            checkboxesItems={
              vehicleConditionFilterProps.conditions
                .condition as SchemaTypes.Condition[]
            }
            registrationMinYear={vehicleConditionFilterProps.conditions.minYear}
            registrationMaxYear={vehicleConditionFilterProps.conditions.maxYear}
            previousOwnersMaxValue={
              vehicleConditionFilterProps.conditions.maxOwners
            }
            selected={{
              minYear: reduxVehicleConditionFilterState.minYear,
              maxYear: reduxVehicleConditionFilterState.maxYear,
              mileage: reduxVehicleConditionFilterState.mileage,
              condition: reduxVehicleConditionFilterState.condition,
              maxOwners: reduxVehicleConditionFilterState.maxOwners,
            }}
            onChange={onVehicleConditionFilterChange}
          />
        </AccordionFilter>
      )}
      {fuelFilterProps && (
        <AccordionFilter
          title={translate(intl, 'ui.PCP.FILTERS.FUEL_TYPE.FUEL_TITLE')}
          id="FacetFuel"
          trackingId="fuel"
          counter={reduxFuelFilterCount}
        >
          <FuelFilter
            fuelTypes={fuelFilterProps.fuelTypes}
            maxFuelConsumption={fuelFilterProps.maxFuelConsumption}
            maxPowerConsumption={fuelFilterProps.maxPowerConsumption}
            value={reduxFuelFilterState}
            onChange={onFuelFilterChange}
          />
        </AccordionFilter>
      )}
      {isArray(driveFilterProps?.power) && (
        <AccordionFilter
          title={translate(intl, 'ui.PCP.FILTERS.DRIVE.ACCORDION_TITLE')}
          id="FacetDrive"
          trackingId="power"
          counter={reduxDriveFilterCount}
        >
          <DriveFilter
            gearBoxList={driveFilterProps.gearBox}
            powerList={
              driveFilterProps.power.map(
                (item) =>
                  item && {
                    ...item,
                    min: Math.ceil(item.min / 10) * 10,
                    max: Math.floor(item.max / 10) * 10,
                  }
              ) as SchemaTypes.DriveFacet['power']
            }
            minCapacity={500}
            maxCapacity={
              Math.floor(driveFilterProps.maxCubicCapacity / 500) * 500
            }
            rangeLabelText={translate(intl, 'ui.PCP.FILTERS.DRIVE.RANGE_LABEL')}
            value={reduxDriveFilterState}
            onChange={onDriveFilterChange}
            darkBg
          />
        </AccordionFilter>
      )}
      {colorListFilterProps && colorListFilterProps.colors && (
        <AccordionFilter
          title={translate(intl, 'ui.PCP.FILTERS.BODY_COLOR.FILTER_LABEL')}
          id="FacetBodyColor"
          trackingId="colour"
          counter={reduxBodyColorsFilterCount}
        >
          <ColorList
            hasShowMore={true}
            colors={colorListFilterProps.colors.map((c) => ({
              ...c,
              isDefaultActive:
                reduxBodyColorsState.colors &&
                reduxBodyColorsState.colors.includes(c.name),
            }))}
            onChange={onBodyColorsFilterChange}
            selected={[...reduxBodyColorsState]}
          />
        </AccordionFilter>
      )}
      {equipmentFilterProps && equipmentFilterProps.equipments && (
        <AccordionFilter
          title={translate(intl, 'ui.PCP.FILTERS.EQUIPMENT.ACCORDION_TITLE')}
          id="FacetEquipment"
          trackingId="equipment"
          counter={reduxEquipmentFilterCount}
        >
          <EquipmentFilter
            onChange={onEquipmentFilterChange}
            equipments={equipmentFilterProps.equipments}
            selected={reduxEquipmentFilterState as EquipmentFilterStateProps}
          ></EquipmentFilter>
        </AccordionFilter>
      )}
      {interiorFilterProps && (
        <AccordionFilter
          title={translate(intl, 'ui.PCP.FILTERS.INTERIOR.FILTER_LABEL')}
          id="FacetInterior"
          trackingId="interior"
          counter={reduxInteriorFilterCount}
        >
          <InteriorFilter
            types={interiorFilterProps.types as SchemaTypes.InteriorType[]}
            colors={interiorFilterProps.colors as SchemaTypes.InteriorColor[]}
            selected={{
              types: reduxInteriorFilterState.interiorType,
              colors: reduxInteriorFilterState.interiorColor,
            }}
            onChange={onInteriorFilterChange}
          />
        </AccordionFilter>
      )}
      {emissionFilterProps && (
        <AccordionFilter
          title={translate(intl, 'ui.PCP.FILTERS.EMISSIONS.EMISSIONS_TITLE')}
          id="FacetEmission"
          trackingId="emission"
          counter={reduxEmissionFilterCount}
        >
          <EmissionFilter
            emissionClass={emissionFilterProps.emissionClass}
            emissionSticker={emissionFilterProps.emissionSticker}
            co2Efficiency={emissionFilterProps.co2Efficiency}
            minCo2Emission={emissionFilterProps.minCo2Emission}
            maxCo2Emission={emissionFilterProps.maxCo2Emission}
            particleFilter={emissionFilterProps.particleFilter}
            value={reduxEmissionFilterState}
            onChange={onEmissionFilterChange}
          />
        </AccordionFilter>
      )}
    </>
  );
};

export default Sidebar;
